/**
 * HomeWhoItsForSection — B2C adaptation of B2B `B2BWorksBestFor`.
 * Solutions · Works best for — staggered 2×2 cards with illustration slots.
 */

import { motion } from 'framer-motion';
import { Briefcase, GraduationCap, RefreshCw, Zap } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { trackCTA } from '../../../utils/metaPixel';

const IMG_EXTS = ['.webp', '.png', '.jpg', '.jpeg', '.svg'] as const;

const ICP_ILLUSTRATION = {
  busy: '/images/landing/works-best-for-b2c/busy-professionals',
  graduate: '/images/landing/works-best-for-b2c/fresh-graduates',
  switcher: '/images/landing/works-best-for-b2c/career-switchers',
  active: '/images/landing/works-best-for-b2c/active-job-seekers',
} as const;

type PersonaCardMeta = {
  icon: React.ElementType;
  illustration: string;
  minH: string;
  illustrationRound?: string;
  iconBg: string;
  cardBg: string;
  border: string;
  illustrationBg: string;
  illustrationBorder: string;
  placeholderIcon: string;
  blob: string;
};

const CARD_META: PersonaCardMeta[] = [
  {
    icon: Briefcase,
    illustration: ICP_ILLUSTRATION.busy,
    minH: 'min-h-0 md:min-h-[320px] lg:min-h-[360px]',
    iconBg: 'bg-violet-500',
    cardBg:
      'bg-gradient-to-br from-violet-50/[0.5] via-white to-white dark:from-violet-950/15 dark:via-[#121214] dark:to-[#121214]',
    border: 'border-violet-100/40 dark:border-violet-900/25',
    illustrationBg: 'bg-violet-50/20 dark:bg-violet-950/15',
    illustrationBorder: 'border-violet-200/35 dark:border-violet-800/25',
    placeholderIcon: 'text-violet-400/55 dark:text-violet-500/45',
    blob: '#8b5cf6',
  },
  {
    icon: GraduationCap,
    illustration: ICP_ILLUSTRATION.graduate,
    minH: 'min-h-0 md:min-h-[220px] lg:min-h-[260px]',
    iconBg: 'bg-rose-500',
    cardBg:
      'bg-gradient-to-br from-rose-50/[0.45] via-white to-white dark:from-rose-950/15 dark:via-[#121214] dark:to-[#121214]',
    border: 'border-rose-100/40 dark:border-rose-900/25',
    illustrationBg: 'bg-rose-50/20 dark:bg-rose-950/15',
    illustrationBorder: 'border-rose-200/35 dark:border-rose-800/25',
    placeholderIcon: 'text-rose-400/55 dark:text-rose-500/45',
    blob: '#f43f5e',
  },
  {
    icon: RefreshCw,
    illustration: ICP_ILLUSTRATION.switcher,
    minH: 'min-h-0 md:min-h-[220px] lg:min-h-[260px]',
    iconBg: 'bg-amber-500',
    cardBg:
      'bg-gradient-to-br from-amber-50/[0.5] via-white to-white dark:from-amber-950/15 dark:via-[#121214] dark:to-[#121214]',
    border: 'border-amber-100/40 dark:border-amber-900/25',
    illustrationBg: 'bg-amber-50/20 dark:bg-amber-950/15',
    illustrationBorder: 'border-amber-200/35 dark:border-amber-800/25',
    placeholderIcon: 'text-amber-400/55 dark:text-amber-500/45',
    blob: '#f59e0b',
  },
  {
    icon: Zap,
    illustration: ICP_ILLUSTRATION.active,
    minH: 'min-h-0 md:min-h-[320px] lg:min-h-[360px]',
    illustrationRound: 'rounded-2xl',
    iconBg: 'bg-blue-600',
    cardBg:
      'bg-gradient-to-br from-blue-50/[0.45] via-white to-white dark:from-blue-950/15 dark:via-[#121214] dark:to-[#121214]',
    border: 'border-blue-100/40 dark:border-blue-900/25',
    illustrationBg: 'bg-blue-50/20 dark:bg-blue-950/15',
    illustrationBorder: 'border-blue-200/35 dark:border-blue-800/25',
    placeholderIcon: 'text-blue-400/55 dark:text-blue-500/45',
    blob: '#2563eb',
  },
];

function IllustrationSlot({
  baseSrc,
  alt,
  bg,
  border,
  iconColor,
  Icon,
  roundClass = 'rounded-xl',
}: {
  baseSrc: string;
  alt: string;
  bg: string;
  border: string;
  iconColor: string;
  Icon: React.ElementType;
  roundClass?: string;
}) {
  const [extIdx, setExtIdx] = useState(0);
  useEffect(() => {
    setExtIdx(0);
  }, [baseSrc]);

  const allExhausted = extIdx >= IMG_EXTS.length;
  const currentSrc = allExhausted ? '' : `${baseSrc}${IMG_EXTS[extIdx]}`;

  if (!allExhausted) {
    return (
      <div className={`flex h-full w-full overflow-hidden ${roundClass}`}>
        <img
          key={currentSrc}
          src={currentSrc}
          alt={alt}
          className={`h-full w-full ${roundClass} object-cover object-center`}
          draggable={false}
          loading="lazy"
          decoding="async"
          onError={() => setExtIdx((i) => i + 1)}
        />
      </div>
    );
  }

  return (
    <div
      className={`flex h-full w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed ${bg} ${border}`}
    >
      <Icon className={`h-12 w-12 ${iconColor}`} strokeWidth={1.25} aria-hidden />
      <span
        className={`max-w-[90%] text-center text-[8px] font-semibold uppercase leading-tight tracking-[0.14em] ${iconColor}`}
      >
        Illustration
        <br />
        <span className="font-mono text-[7px] font-normal normal-case tracking-normal opacity-70">
          {baseSrc}.(webp|png|jpg)
        </span>
      </span>
    </div>
  );
}

function PersonaCardContent({
  meta,
  title,
  body,
  Icon,
}: {
  meta: PersonaCardMeta;
  title: string;
  body: string;
  Icon: React.ElementType;
}) {
  return (
    <div className="relative flex h-full flex-col md:block">
      <div className="relative z-10 order-2 flex flex-col p-5 sm:p-6 md:order-none md:h-full md:p-7 md:pr-[57%]">
        <div className={`mb-3 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl md:mb-4 ${meta.iconBg} shadow-sm`}>
          <Icon className="h-5 w-5 text-white" strokeWidth={2} aria-hidden />
        </div>
        <h3 className="text-lg font-bold leading-snug text-gray-900 dark:text-white md:text-xl">{title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-400 md:text-[15px]">{body}</p>
      </div>

      <div className="relative order-1 h-44 w-full shrink-0 p-4 pb-0 sm:h-48 md:absolute md:bottom-0 md:right-0 md:top-0 md:h-auto md:w-[54%] md:p-4">
        <IllustrationSlot
          baseSrc={meta.illustration}
          alt={`${title} illustration`}
          bg={meta.illustrationBg}
          border={meta.illustrationBorder}
          iconColor={meta.placeholderIcon}
          Icon={Icon}
          roundClass={meta.illustrationRound}
        />
      </div>

      <div
        className="pointer-events-none absolute -bottom-8 -right-6 hidden h-36 w-36 rounded-full opacity-[0.07] blur-2xl md:block"
        style={{ background: meta.blob }}
        aria-hidden
      />
    </div>
  );
}

interface HomeWhoItsForSectionProps {
  onNavigate?: (view: 'home' | 'login' | 'signup' | 'contact-us') => void;
}

const TAB_LABELS = ['Busy Pros', 'Graduates', 'Switchers', 'Active'];

const HomeWhoItsForSection: React.FC<HomeWhoItsForSectionProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [mobileActiveTab, setMobileActiveTab] = useState(0);
  const personas = t('home.whoItsFor.personas', { returnObjects: true }) as { title: string; body: string }[];

  const handleCta = () => {
    trackCTA('home_who_its_for_cta');
    onNavigate?.('signup');
  };

  const cards = CARD_META.map((meta, i) => ({
    meta,
    ...(Array.isArray(personas) && personas[i] ? personas[i] : { title: '', body: '' }),
  }));

  const mobileCard = cards[mobileActiveTab];

  return (
    <section id="who-its-for" aria-labelledby="who-its-for-title" className="home-who-its-for-section scroll-mt-28">
      <div className="mx-auto w-full max-w-[1600px] px-4 sm:px-8 md:px-10 lg:px-12 xl:px-16">
        <motion.header
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="hiw__header mb-8 md:mb-10"
        >
          <p className="hiw__eyebrow">{t('home.whoItsFor.eyebrow')}</p>
          <h2 id="who-its-for-title" className="hiw__title text-balance">
            {t('home.whoItsFor.titleBefore')}{' '}
            <span className="hiw__title-accent">{t('home.whoItsFor.titleAccent')}</span>
          </h2>
          <p className="hiw__subtitle text-balance">{t('home.whoItsFor.subtitle')}</p>
        </motion.header>

        {/* ── Mobile: horizontal tab selector — one card at a time ── */}
        <div className="md:hidden">
          <div className="flex gap-2 mb-4 overflow-x-auto pb-1" style={{ scrollSnapType: 'x mandatory' }}>
            {cards.map((card, i) => {
              const Icon = card.meta.icon;
              const isActive = mobileActiveTab === i;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => setMobileActiveTab(i)}
                  className="flex items-center gap-1.5 shrink-0 px-3 py-2 rounded-full text-xs font-semibold transition-all"
                  style={{
                    scrollSnapAlign: 'start',
                    background: isActive ? `${card.meta.blob}18` : 'var(--ds-bg-tinted, #f1f5f9)',
                    color: isActive ? card.meta.blob : 'var(--ds-text-muted, #64748b)',
                    border: isActive ? `1.5px solid ${card.meta.blob}40` : '1.5px solid transparent',
                  }}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {TAB_LABELS[i]}
                </button>
              );
            })}
          </div>

          {mobileCard?.title && (
            <motion.article
              key={mobileActiveTab}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
              className={`group relative overflow-hidden rounded-[1.75rem] border shadow-sm ${mobileCard.meta.cardBg} ${mobileCard.meta.border}`}
            >
              <PersonaCardContent
                meta={mobileCard.meta}
                title={mobileCard.title}
                body={mobileCard.body}
                Icon={mobileCard.meta.icon}
              />
            </motion.article>
          )}
        </div>

        {/* ── Desktop: original 2-column stacked cards ── */}
        <div className="hidden md:flex flex-col gap-5 lg:flex-row lg:items-start lg:gap-6">
          <div className="flex flex-1 flex-col gap-5 md:gap-6">
            {[cards[0], cards[2]].filter(Boolean).map((card, colIdx) => {
              if (!card?.title) return null;
              const Icon = card.meta.icon;
              return (
                <motion.article
                  key={card.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: colIdx === 0 ? 0 : 0.14 }}
                  className={`group relative overflow-hidden rounded-[1.75rem] border shadow-sm transition-shadow duration-300 hover:shadow-md md:rounded-[2rem] ${card.meta.minH} ${card.meta.cardBg} ${card.meta.border}`}
                >
                  <PersonaCardContent
                    meta={card.meta}
                    title={card.title}
                    body={card.body}
                    Icon={Icon}
                  />
                </motion.article>
              );
            })}
          </div>

          <div className="flex flex-1 flex-col gap-5 md:gap-6">
            {[cards[1], cards[3]].filter(Boolean).map((card, colIdx) => {
              if (!card?.title) return null;
              const Icon = card.meta.icon;
              return (
                <motion.article
                  key={card.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: colIdx === 0 ? 0.07 : 0.21 }}
                  className={`group relative overflow-hidden rounded-[1.75rem] border shadow-sm transition-shadow duration-300 hover:shadow-md md:rounded-[2rem] ${card.meta.minH} ${card.meta.cardBg} ${card.meta.border}`}
                >
                  <PersonaCardContent
                    meta={card.meta}
                    title={card.title}
                    body={card.body}
                    Icon={Icon}
                  />
                </motion.article>
              );
            })}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-10 flex justify-center md:mt-12"
        >
          <button
            type="button"
            onClick={handleCta}
            className="home-marketing-cta"
          >
            {t('home.whoItsFor.cta')}
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default HomeWhoItsForSection;
